import { Injectable } from '@angular/core';
import { Quiz, highlightText } from 'funnel/src/app/data/quiz';
import {
  FunnelFlowService,
  QuizStepKey,
} from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { shortFunnelFlow } from '../short-funnel.flow';

declare const $localize: any;

@Injectable({
  providedIn: 'root',
})
export class ShortFunnelFlowService extends FunnelFlowService {
  // The first screen stays as designed; an ad's `?title=` is ignored here.
  protected override readonly isCustomIntroTitleSupported = false;

  protected override getFlowConfig(): FunnelFlowConfig {
    return shortFunnelFlow;
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key === 'format') {
      return {
        ...this.steps.format,
        subtitle: $localize`:@@shortFormatSubtitle:This will not limit your experience`,
      };
    }

    if (key === 'goals') {
      return {
        ...this.steps.goals,
        title:
          $localize`:@@shortGoalsTitlePrefix:What do you need ` +
          highlightText($localize`:@@aiTextToSpeech:AI text-to-speech`) +
          $localize`:@@shortGoalsTitleSuffix: for?`,
      };
    }

    if (key === 'goalInfo') {
      const data = super.customizeStepData(key) || this.steps.goalInfo;
      return {
        ...data,
        title: this.getGoalInfoTitle(),
        subtitle: this.getGoalInfoSubtitle(),
      };
    }

    return super.customizeStepData(key);
  }

  private getGoalInfoTitle(): string {
    const goalId = this.getGoalId();

    if (!goalId) {
      return (
        $localize`:@@shortGoalInfoDefaultPrefix:Unlock your next breakthrough with ` +
        highlightText($localize`:@@tailoredAudioLearning:tailored audio learning`)
      );
    }

    switch (goalId) {
      case 'beMoreProductive':
        return (
          $localize`:@@shortGoalInfoProductivePrefix:Unlock ` +
          highlightText($localize`:@@peakProductivity:peak productivity`) +
          $localize`:@@shortGoalInfoProductiveSuffix: without adding hours to your day`
        );
      case 'learnSomethingNew':
        return (
          $localize`:@@shortGoalInfoLearnPrefix:Make ` +
          highlightText($localize`:@@everyLessonStick:every lesson stick`) +
          $localize`:@@shortGoalInfoLearnSuffix: with audio you can absorb anywhere`
        );
      case 'listenOnTheGo':
        return (
          $localize`:@@shortGoalInfoListenPrefix:Turn every commute into your ` +
          highlightText($localize`:@@personalGrowthPlaylist:personal growth playlist`)
        );
      case 'makeReadingEasier':
        return (
          $localize`:@@shortGoalInfoReadingPrefix:Transform reading into ` +
          highlightText($localize`:@@effortlessListening:effortless listening`) +
          $localize`:@@shortGoalInfoReadingSuffix: in seconds`
        );
      case 'adhdEyeStrainDyslexia':
        return (
          $localize`:@@shortGoalInfoAdhdPrefix:Give your brain a ` +
          highlightText($localize`:@@focusFriendlyCoach:focus-friendly audio coach`)
        );
      case 'everythingIsPerfect':
      default:
        return (
          $localize`:@@shortGoalInfoPerfectPrefix:Stay ahead with ` +
          highlightText($localize`:@@audioRoutineBuilt:an audio routine`) +
          $localize`:@@shortGoalInfoPerfectSuffix: built around you`
        );
    }
  }
}
